import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, first_name?: string, last_name?: string) => Promise<void>;
  googleLogin: (idToken: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('authToken'));
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const initializeAuth = async () => {
      if (token) {
        try {
          const userData = await authAPI.getProfile();
          setUser(userData);
        } catch (error) {
          console.error('Failed to get user profile:', error);
          // Token is invalid, clear it
          localStorage.removeItem('authToken');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, [token]);

  const clearOnboardingFlags = () => {
    localStorage.removeItem('wealthini_onboarding_complete');
    localStorage.removeItem('wealthini_no_business');
    localStorage.removeItem('wealthini_checklist_dismissed');
    localStorage.removeItem('wealthini_force_onboarding');
    localStorage.removeItem('wealthini_show_welcome_hint');
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await authAPI.login({ email, password });
      setUser(response.user);
      setToken(response.token);
      localStorage.setItem('authToken', response.token);
      // Redirect to dashboard after successful login
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const register = async (email: string, password: string, first_name?: string, last_name?: string) => {
    try {
      const response = await authAPI.register({ email, password, first_name, last_name });
      setUser(response.user);
      setToken(response.token);
      localStorage.setItem('authToken', response.token);
      clearOnboardingFlags();
      // Redirect to onboarding after successful registration
      navigate('/onboarding', { replace: true });
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  };

  const googleLogin = async (idToken: string) => {
    try {
      const response = await authAPI.googleAuth(idToken);
      setUser(response.user);
      setToken(response.token);
      localStorage.setItem('authToken', response.token);
      
      // Redirect to dashboard after successful Google login
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Google login failed:', error);
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('authToken');
    clearOnboardingFlags();
    // Redirect to login page
    navigate('/login', { replace: true });
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    googleLogin,
    logout,
    loading,
    isAuthenticated: !!user && !!token,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 